import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

interface Definition {
  customFieldDefinitionId: string;
  entityType: string;
  fieldKey: string;
  labelAr: string;
  fieldType: number;
  options: string | null;
  isRequired: boolean;
  isActive: boolean;
}

const ENTITY_TYPES = ['Item', 'Customer', 'Supplier', 'Employee', 'Project', 'SalesOrder'];
const FIELD_TYPES = [
  { value: '1', key: 'text' },
  { value: '2', key: 'number' },
  { value: '3', key: 'date' },
  { value: '4', key: 'boolean' },
  { value: '5', key: 'select' },
];

export function CustomFieldsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [entityType, setEntityType] = useState('Item');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cf-defs', entityType] as const,
    queryFn: async () =>
      (await apiClient.get<Definition[]>('/api/custom-fields/definitions', {
        params: { entityType, includeInactive: true },
      })).data,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/api/custom-fields/definitions/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cf-defs', entityType] }),
  });

  const typeLabel = (ft: number) => t(`customFields.type.${FIELD_TYPES.find((x) => x.value === String(ft))?.key ?? 'text'}`);

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.organization')} title={t('customFields.title')}
        subtitle={t('customFields.subtitle')} />

      <Paper p="md">
        <Group justify="space-between" wrap="wrap">
          <Select label={t('customFields.entityType')} value={entityType} onChange={(v) => setEntityType(v ?? 'Item')}
            data={ENTITY_TYPES.map((e) => ({ value: e, label: t(`customFields.entities.${e}`) }))} w={220} />
          <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />} mt="lg"
            onClick={() => setCreateOpen(true)}>{t('customFields.new')}</Button>
        </Group>
      </Paper>

      <Paper p="md">
        {isLoading ? <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
          : (data ?? []).length === 0 ? <Text size="sm" c="dimmed" ta="center" py="lg">{t('customFields.empty')}</Text>
          : (
          <Table verticalSpacing="sm">
            <Table.Thead><Table.Tr>
              <Table.Th>{t('customFields.label')}</Table.Th>
              <Table.Th>{t('customFields.key')}</Table.Th>
              <Table.Th>{t('customFields.fieldType')}</Table.Th>
              <Table.Th>{t('customFields.required')}</Table.Th>
              <Table.Th>{t('customFields.active')}</Table.Th>
              <Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((d) => (
                <Table.Tr key={d.customFieldDefinitionId}>
                  <Table.Td>{d.labelAr}</Table.Td>
                  <Table.Td dir="ltr" className="font-mono">{d.fieldKey}</Table.Td>
                  <Table.Td>{typeLabel(d.fieldType)}</Table.Td>
                  <Table.Td>{d.isRequired ? '✓' : '—'}</Table.Td>
                  <Table.Td><Badge variant="light" color={d.isActive ? 'green' : 'gray'}>
                    {d.isActive ? t('customFields.on') : t('customFields.off')}</Badge></Table.Td>
                  <Table.Td><Group justify="flex-end">
                    <ActionIcon variant="subtle" color="red" aria-label={t('common.delete')}
                      onClick={() => remove.mutate(d.customFieldDefinitionId)}>
                      <IconTrash size={16} stroke={1.6} />
                    </ActionIcon>
                  </Group></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CreateModal opened={createOpen} entityType={entityType} onClose={() => setCreateOpen(false)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['cf-defs', entityType] })} />
    </Stack>
  );
}

function CreateModal({ opened, entityType, onClose, onSaved }: {
  opened: boolean; entityType: string; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [labelAr, setLabelAr] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldType, setFieldType] = useState('1');
  const [options, setOptions] = useState('');
  const [required, setRequired] = useState(false);

  const save = useMutation({
    mutationFn: async () => apiClient.post('/api/custom-fields/definitions', {
      entityType, fieldKey: fieldKey.trim(), labelAr: labelAr.trim(), labelEn: null,
      fieldType: Number(fieldType), options: fieldType === '5' ? options.trim() : null,
      isRequired: required, sortOrder: 0, isActive: true,
    }),
    onSuccess: () => { reset(); onSaved(); onClose(); },
    onError: (err) => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'),
      message: (err as { response?: { data?: { title?: string } } }).response?.data?.title ?? '' }),
  });
  const reset = () => { setLabelAr(''); setFieldKey(''); setFieldType('1'); setOptions(''); setRequired(false); };

  return (
    <Modal opened={opened} onClose={onClose} title={t('customFields.new')}>
      <Stack gap="sm">
        <TextInput label={t('customFields.label')} value={labelAr} onChange={(e) => setLabelAr(e.currentTarget.value)} withAsterisk />
        <TextInput label={t('customFields.key')} description={t('customFields.keyHelp')} dir="ltr"
          value={fieldKey} onChange={(e) => setFieldKey(e.currentTarget.value)} withAsterisk />
        <Select label={t('customFields.fieldType')} value={fieldType} onChange={(v) => setFieldType(v ?? '1')}
          data={FIELD_TYPES.map((x) => ({ value: x.value, label: t(`customFields.type.${x.key}`) }))} />
        {fieldType === '5' && (
          <TextInput label={t('customFields.options')} description={t('customFields.optionsHelp')}
            value={options} onChange={(e) => setOptions(e.currentTarget.value)} />
        )}
        <Switch label={t('customFields.required')} checked={required} onChange={(e) => setRequired(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={save.isPending} disabled={!labelAr.trim() || fieldKey.trim().length < 2}
            onClick={() => save.mutate()}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
